import { useSyncExternalStore } from 'react';

/**
 * The tenant's `/portal/config` payload, minus branding and the time zone,
 * which already have homes (`brandStore`, `clinicClock`).
 *
 * Two things downstream need it. `modules` gates whole features — a tenant
 * without `telemedicine` must not be offered a video visit at all. And
 * `telemedPatientSummaryMode` decides whether the AI visit summary exists as a
 * concept for this tenant: `OFF` means the patient never sees the surface, not
 * that they see an empty one.
 *
 * External store rather than context for the same reason `clinicClock` is one:
 * `BrandGate` fetches the config well above the routes that read it, and a
 * context would force every consumer to sit under a provider it does not
 * otherwise need.
 */
export type TelemedPatientSummaryMode = 'OFF' | 'CLINICIAN_RELEASE' | 'AUTO';

export type PortalConfigState = {
  modules: string[];
  telemedPatientSummaryMode: TelemedPatientSummaryMode;
};

const EMPTY: PortalConfigState = { modules: [], telemedPatientSummaryMode: 'OFF' };

let state: PortalConfigState = EMPTY;
const listeners = new Set<() => void>();

function normalizeMode(value: unknown): TelemedPatientSummaryMode {
  return value === 'AUTO' || value === 'CLINICIAN_RELEASE' ? value : 'OFF';
}

function normalizeModules(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
}

function sameModules(left: string[], right: string[]) {
  return left.length === right.length && left.every((item, index) => item === right[index]);
}

/**
 * Publishes a fresh `/portal/config`. Called from `BrandGate` beside
 * `setClinicTimeZone`; a network failure keeps the last-known-good state, so
 * this is only ever reached with a successful response.
 */
export function setPortalConfig(config: { modules?: unknown; telemedPatientSummaryMode?: unknown } | null | undefined) {
  const modules = normalizeModules(config?.modules);
  const telemedPatientSummaryMode = normalizeMode(config?.telemedPatientSummaryMode);
  if (sameModules(modules, state.modules) && telemedPatientSummaryMode === state.telemedPatientSummaryMode) return;
  state = { modules, telemedPatientSummaryMode };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const get = () => state;

export function usePortalConfig(): PortalConfigState {
  return useSyncExternalStore(subscribe, get, get);
}

export function usePortalModules(): string[] {
  return useSyncExternalStore(subscribe, get, get).modules;
}

export function useTelemedPatientSummaryMode(): TelemedPatientSummaryMode {
  return useSyncExternalStore(subscribe, get, get).telemedPatientSummaryMode;
}

/** Reads the current config outside React (guards, one-shot checks). */
export function getPortalConfig(): PortalConfigState {
  return state;
}

export function hasPortalModule(module: string): boolean {
  return state.modules.includes(module);
}
